import React, { ReactElement } from 'react';
import cx from 'classnames';
import ReactList from 'react-list';
import { withTheme } from '@ifit/themes/withTheme';

import './index.scss';

interface ISimpleGrid {
  className: string;
  style: React.CSSProperties;
  itemRenderer: () => React.ReactNode;
  length: number;
}

const SimpleGrid = ({
  className,
  style,
  itemRenderer,
  length,
}: ISimpleGrid): ReactElement => {
  return (
    <div className={className} style={style}>
      {[...Array(length).keys()].map(itemRenderer)}
    </div>
  );
};

interface IGridList<T> {
  children: (props: T) => React.ReactNode;
  data: T[];
  gridGap?: number;
  minWidth?: number;
  className?: string;
  deviceType?: string;
  containsLibraryCards?: boolean;
  containsLibraryBadges?: boolean;
  useVirtual?: boolean;
  themeClass?: string;
}

const GridList = <T extends unknown>({
  data,
  children,
  gridGap = 24,
  minWidth = 216,
  className = '',
  containsLibraryCards = false,
  containsLibraryBadges = false,
  deviceType,
  useVirtual,
  themeClass,
}: IGridList<T>): ReactElement | null => {
  if (!data) return null;

  const supportsGrid =
    !useVirtual && window.CSS && CSS.supports('display', 'grid');

  const style = {
    gridTemplateColumns:
      supportsGrid && minWidth !== null
        ? `repeat(auto-fill, minmax(${minWidth}px, 1fr))`
        : null,
    gridGap: supportsGrid ? gridGap : null,
    margin: supportsGrid
      ? 0
      : useVirtual
      ? `${20 - gridGap / 2}px ${16 - gridGap / 2}px 0`
      : `0 ${-gridGap / 2}px`,
  };

  const cardStyles = {
    padding: supportsGrid ? 0 : gridGap / 2,
  };

  const renderCell = item => (
    <div className="grid-item" style={cardStyles} key={`grid-${item}`}>
      {children(data[item])}
    </div>
  );

  const Grid = useVirtual ? ReactList : SimpleGrid;

  const classNames = cx(
    'grid-list',
    themeClass,
    className,
    'is-' + (deviceType || 'tablet'),
    {
      'contains-cards': containsLibraryCards,
      'contains-badges': containsLibraryBadges,
    }
  );

  return (
    <Grid
      className={classNames}
      style={style}
      length={data.length}
      itemRenderer={renderCell}
      type="uniform"
      threshold={600}
      itemsRenderer={(items, ref) => (
        <div
          ref={ref}
          style={style as React.CSSProperties}
          className={classNames}
        >
          {items}
        </div>
      )}
    />
  );
};

export default withTheme(GridList);
